/**
 * Lightweight Godot-style area/body overlap using world AABBs.
 */

import * as THREE from 'three';
import type { TreeNodeLink } from './ScriptRuntime';
import type { SceneNodeData } from './types';

export interface ColliderInfo {
  nodeId: string;
  link: TreeNodeLink;
  kind: 'area' | 'body' | 'static';
  box: THREE.Box3;
  layer: number;
  mask: number;
  disabled: boolean;
}

function shapeBox(node: SceneNodeData, worldPos: THREE.Vector3): THREE.Box3 {
  const size = node.props.shapeSize || { x: 0.5, y: 0.5, z: 0.5 };
  const shape = node.props.shape || 'box';
  let hx = Math.abs(size.x) * 0.5;
  let hy = Math.abs(size.y) * 0.5;
  let hz = Math.abs(size.z) * 0.5;
  if (shape === 'sphere') {
    const r = Math.max(hx, hy, hz) * 2 || 0.5;
    hx = hy = hz = r;
  } else if (shape === 'capsule') {
    hx = Math.max(hx, hz);
    hz = hx;
  }
  // Scale by node scale
  const s = node.transform.scale;
  hx *= Math.abs(s.x);
  hy *= Math.abs(s.y);
  hz *= Math.abs(s.z);
  const min = new THREE.Vector3(worldPos.x - hx, worldPos.y - hy, worldPos.z - hz);
  const max = new THREE.Vector3(worldPos.x + hx, worldPos.y + hy, worldPos.z + hz);
  return new THREE.Box3(min, max);
}

function worldPosition(link: TreeNodeLink): THREE.Vector3 {
  if (link.object3d) {
    const v = new THREE.Vector3();
    link.object3d.getWorldPosition(v);
    return v;
  }
  return new THREE.Vector3(
    link.data.transform.position.x,
    link.data.transform.position.y,
    link.data.transform.position.z,
  );
}

/** Find CollisionShape3D children and build collider for parent body/area. */
export function collectColliders(links: Map<string, TreeNodeLink>): ColliderInfo[] {
  const out: ColliderInfo[] = [];
  for (const link of links.values()) {
    const t = link.data.type;
    if (t !== 'Area3D' && t !== 'CharacterBody3D' && t !== 'StaticBody3D') continue;

    // Prefer explicit CollisionShape3D children
    const shapes = link.children.filter((c) => c.data.type === 'CollisionShape3D');
    if (shapes.length) {
      for (const sh of shapes) {
        if (sh.data.props.disabled) continue;
        const wp = worldPosition(sh);
        out.push({
          nodeId: link.data.id,
          link,
          kind: t === 'Area3D' ? 'area' : t === 'StaticBody3D' ? 'static' : 'body',
          box: shapeBox(sh.data, wp),
          layer: (link.data.props.collisionLayer as number) || 1,
          mask: (link.data.props.collisionMask as number) || 1,
          disabled: false,
        });
      }
    } else {
      // Default capsule for characters / box for areas
      const fake: SceneNodeData = {
        ...link.data,
        props: {
          ...link.data.props,
          shape: t === 'CharacterBody3D' ? 'capsule' : 'box',
          shapeSize:
            t === 'CharacterBody3D'
              ? { x: 0.35, y: 1.0, z: 0.35 }
              : { x: 1, y: 1, z: 1 },
        },
      };
      out.push({
        nodeId: link.data.id,
        link,
        kind: t === 'Area3D' ? 'area' : t === 'StaticBody3D' ? 'static' : 'body',
        box: shapeBox(fake, worldPosition(link)),
        layer: (link.data.props.collisionLayer as number) || 1,
        mask: (link.data.props.collisionMask as number) || 1,
        disabled: false,
      });
    }
  }
  return out;
}

function layersMatch(a: ColliderInfo, b: ColliderInfo): boolean {
  return ((a.mask & b.layer) !== 0 || (b.mask & a.layer) !== 0);
}

/**
 * Update area overlaps; emits body_entered / body_exited on Area3D scripts.
 * Returns current overlapping pairs areaId -> Set<bodyId>
 */
export function updateAreaOverlaps(
  colliders: ColliderInfo[],
  prev: Map<string, Set<string>>,
): Map<string, Set<string>> {
  const next = new Map<string, Set<string>>();
  const areas = colliders.filter((c) => c.kind === 'area' && c.link.data.props.monitoring !== false);
  const bodies = colliders.filter((c) => c.kind === 'body' || c.kind === 'static');

  for (const area of areas) {
    const set = new Set<string>();
    for (const body of bodies) {
      if (body.nodeId === area.nodeId) continue;
      if (!layersMatch(area, body)) continue;
      if (area.box.intersectsBox(body.box)) set.add(body.nodeId);
    }
    next.set(area.nodeId, set);

    const was = prev.get(area.nodeId) || new Set();
    const api = area.link.script?.api;
    if (api) {
      for (const id of set) {
        if (!was.has(id)) {
          const bodyLink = bodies.find((b) => b.nodeId === id)?.link;
          const bodyApi = bodyLink?.script?.api ?? { name: bodyLink?.data.name, id };
          try {
            api.emit_signal('body_entered', bodyApi);
          } catch {
            /* */
          }
        }
      }
      for (const id of was) {
        if (!set.has(id)) {
          const bodyLink = bodies.find((b) => b.nodeId === id)?.link;
          const bodyApi = bodyLink?.script?.api ?? { name: bodyLink?.data.name, id };
          try {
            api.emit_signal('body_exited', bodyApi);
          } catch {
            /* */
          }
        }
      }
    }
  }
  return next;
}

/** Push CharacterBody3D out of StaticBody3D overlaps (simple AABB resolve on XZ). */
export function resolveStaticCollisions(colliders: ColliderInfo[]): void {
  const bodies = colliders.filter((c) => c.kind === 'body');
  const statics = colliders.filter((c) => c.kind === 'static');
  for (const body of bodies) {
    for (const st of statics) {
      if (!layersMatch(body, st)) continue;
      if (!body.box.intersectsBox(st.box)) continue;
      // Resolve on X primarily (piste strip)
      const bc = body.box.getCenter(new THREE.Vector3());
      const sc = st.box.getCenter(new THREE.Vector3());
      const dx = bc.x - sc.x;
      const push = (body.box.max.x - body.box.min.x) * 0.5 + (st.box.max.x - st.box.min.x) * 0.5 - Math.abs(dx) + 0.01;
      if (push > 0 && body.link.data) {
        const sign = dx >= 0 ? 1 : -1;
        body.link.data.transform.position.x += sign * push;
        if (body.link.object3d) {
          body.link.object3d.position.x = body.link.data.transform.position.x;
        }
      }
    }
  }
}
